import { CreateVisitasPlanItemDto } from '../../../types/visitas';
import { DireccionCliente } from '../../../interfaces/DireccionCliente';
import { VisitasAgendaDiaKey, VisitasAgendaDias } from '../../../types/visitasAgenda';
import { PlanVisitCreateDraft } from '../../../types/visitas';

export interface PlanVisitRowDraft {
  item: CreateVisitasPlanItemDto;
  horarioPreferido: string;
}

export const truncateLabel = (text: string, max = 90): string =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

export const buildFechaHoraProgramada = (fechaPlan: string, horario: string): string | null => {
  const h = horario.trim();
  if (!fechaPlan || !h) return null;
  return `${fechaPlan}T${h.length === 5 ? `${h}:00` : h}`;
};

export const sortPlanVisitRows = (rows: PlanVisitRowDraft[]): PlanVisitRowDraft[] => {
  const withIndex = rows.map((row, index) => ({ row, index }));
  const withTime = withIndex
    .filter(({ row }) => Boolean(row.horarioPreferido.trim()))
    .sort(
      (a, b) =>
        a.row.horarioPreferido.localeCompare(b.row.horarioPreferido) || a.index - b.index,
    );
  const withoutTime = withIndex
    .filter(({ row }) => !row.horarioPreferido.trim())
    .sort((a, b) => a.index - b.index);
  return [...withTime, ...withoutTime].map(({ row }) => row);
};

export const buildCreatePlanItemsPayload = (
  fechaPlan: string,
  rows: PlanVisitRowDraft[],
): CreateVisitasPlanItemDto[] =>
  sortPlanVisitRows(rows.filter((row) => Number(row.item.nroCliente) > 0)).map((row, idx) => ({
    nroCliente: Number(row.item.nroCliente),
    codigoDireccion: row.item.codigoDireccion?.trim() || undefined,
    fechaHoraProgramada: buildFechaHoraProgramada(fechaPlan, row.horarioPreferido),
    observaciones: row.item.observaciones || null,
    orden: idx + 1,
  }));

const MAX_PLAN_RANGE_DAYS = 366;

export const eachIsoDayInRange = (fechaDesde: string, fechaHasta: string): string[] => {
  if (!fechaDesde || !fechaHasta || fechaDesde > fechaHasta) return [];

  const days: string[] = [];
  const cursor = new Date(`${fechaDesde}T12:00:00`);
  const end = new Date(`${fechaHasta}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return [];

  while (cursor <= end) {
    const yyyy = cursor.getFullYear();
    const mm = String(cursor.getMonth() + 1).padStart(2, '0');
    const dd = String(cursor.getDate()).padStart(2, '0');
    days.push(`${yyyy}-${mm}-${dd}`);
    if (days.length > MAX_PLAN_RANGE_DAYS) return [];
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

export const countIsoDaysInRange = (fechaDesde: string, fechaHasta: string): number =>
  eachIsoDayInRange(fechaDesde, fechaHasta).length;

const ISO_DOW_TO_AGENDA_KEY: Record<number, VisitasAgendaDiaKey> = {
  0: 'dom',
  1: 'lun',
  2: 'mar',
  3: 'mier',
  4: 'jue',
  5: 'vie',
  6: 'sab',
};

export const isoDayMatchesAgendaDias = (isoDay: string, dias: VisitasAgendaDias): boolean => {
  const date = new Date(`${isoDay}T12:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const key = ISO_DOW_TO_AGENDA_KEY[date.getDay()];
  return key ? Boolean(dias[key]) : false;
};

export const candidatoAppliesInRange = (
  dias: VisitasAgendaDias,
  fechaDesde: string,
  fechaHasta: string,
): boolean => eachIsoDayInRange(fechaDesde, fechaHasta).some((day) => isoDayMatchesAgendaDias(day, dias));

export const shouldIncludeDraftOnDay = (_draft: PlanVisitCreateDraft, _isoDay: string): boolean => true;

export const visitRowKey = (nroCliente: number, codigoDireccion?: string): string =>
  `${nroCliente}|${(codigoDireccion ?? '').trim()}`;

export const pickDireccionLabel = (d: DireccionCliente): string =>
  truncateLabel([d.codigoDireccion, d.direccion].filter(Boolean).join(' — ') || d.codigoDireccion);
